import clsx from "clsx";
import React from "react";
import SecondaryButton from "web/react/components/buttons/secondary-button/secondary-button";
import LeadLink from "web/react/components/lead-link/lead-link";
import { Button } from "web/react/emo/button/button";
import { Heading } from "web/react/emo/heading/heading";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import { useProductClick } from "web/react/hooks/use-product-click/use-product-click";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import browser from "web/script/utils/browser";
import styles from "./related-products-sidebar.module.css";

interface RelatedProductImageCardProps {
    product: ProductImageCard;
    isForNewPDPDesktopViewport?: boolean;
}

export interface RelatedProductsSidebarInterface {
    product_image_cards: ProductImageCard[];
    view_more_url: string;
}

export interface RelatedProductsSidebarProps {
    className?: string;
    relatedProductsSidebar?: RelatedProductsSidebarInterface | null;
    variant?: "vertical" | "horizontal";
}

function RelatedProductImageCard({
    product,
    isForNewPDPDesktopViewport = false,
}: RelatedProductImageCardProps): React.ReactElement {
    const { onClick } = useProductClick({
        reason: product.context_type,
        productSlug: product.product_slug,
        isLeadLink: !!product.lead_url,
        href: product.lead_url || product.url,
        openInNewTab: !!product.lead_url,
    });

    function beforeOnClick(callback, event): void {
        onClick(event);
        callback(event);
    }

    return (
        <div className={styles.product}>
            {product.lead_url ? (
                <LeadLink
                    className={clsx(styles.cardLink, {
                        [styles.isForNewPDPDesktopViewport]: isForNewPDPDesktopViewport,
                    })}
                    href={product.lead_url}
                    reason={product.context_type}
                    productId={String(product.id)}
                    beforeOnClick={beforeOnClick}
                    rel="nofollow"
                    openInNewTab
                >
                    <img
                        className={styles.cardImage}
                        src={product.image_url}
                        height={product.image_height}
                        width={product.image_width}
                        alt={product.image_alt_text}
                    />
                </LeadLink>
            ) : (
                <a
                    className={clsx(styles.cardLink, {
                        [styles.isForNewPDPDesktopViewport]: isForNewPDPDesktopViewport,
                    })}
                    href={product.url}
                    onClick={onClick}
                >
                    <img
                        className={styles.cardImage}
                        src={product.image_url}
                        height={product.image_height}
                        width={product.image_width}
                        alt={product.image_alt_text}
                    />
                </a>
            )}
        </div>
    );
}

function RelatedProductsSidebar({
    className,
    relatedProductsSidebar,
    variant = "vertical",
}: RelatedProductsSidebarProps): React.ReactElement | null {
    const clickSeeMore = useClickTracker("navigation", "see_more_related", "sidebar", true);
    const emoRedesign = Boolean(environment.getFeature("cw_pdp_redesign"));

    if (!relatedProductsSidebar) {
        return null;
    }

    const isForNewPDPDesktopViewport =
        emoRedesign && browser.getViewport() === browser.VIEWPORTS.DESKTOP;

    const SeeMoreButton = (): React.ReactElement =>
        isForNewPDPDesktopViewport ? (
            <Button
                size="sm"
                href={relatedProductsSidebar?.view_more_url}
                onClick={clickSeeMore}
                variant="secondary"
                width="fit-content"
                title={gettext("in_stock_product.see_more_related_btn_label")}
            />
        ) : (
            <div className="text-center">
                <SecondaryButton
                    small
                    href={relatedProductsSidebar.view_more_url}
                    onClick={clickSeeMore}
                    className="text-center"
                    fullWidth
                >
                    {gettext("in_stock_product.see_more_related_btn_label")}
                </SecondaryButton>
            </div>
        );

    return (
        <aside className={className}>
            <section
                className={clsx({
                    [styles.sidebar]: variant === "vertical",
                    [styles.horizontal]: variant === "horizontal",
                })}
            >
                <Heading
                    textStyle="title-1"
                    as="h5"
                    className={clsx(styles.title, {
                        [styles.compact]: isForNewPDPDesktopViewport,
                    })}
                >
                    {isForNewPDPDesktopViewport
                        ? gettext("product.buy_area.more_like_this.label")
                        : gettext("in_stock_product.related")}
                </Heading>
                <div
                    className={clsx({
                        [styles.inner]: !isForNewPDPDesktopViewport,
                        [styles.compact]: isForNewPDPDesktopViewport,
                    })}
                >
                    {relatedProductsSidebar.product_image_cards.map((product) => (
                        <RelatedProductImageCard
                            key={product.id}
                            product={product}
                            isForNewPDPDesktopViewport={isForNewPDPDesktopViewport}
                        />
                    ))}
                    {variant === "vertical" && <SeeMoreButton />}
                </div>
            </section>
        </aside>
    );
}

export default RelatedProductsSidebar;
